import { createContext, ReactNode, useContext, useState } from "react";

import { FetchResult, useLazyQuery, useMutation, useSubscription } from "@apollo/client";

import { UP_CONNECT_DEVICE_UPDATE_TASK } from "../../app/graphql/mutations";
import { GET_DEVICES_CONNECTED_TO_USER } from "../../app/graphql/queries";
import { UP_CONNECT_DEVICE_SUBSCRIPTION } from "../../app/graphql/subscriptions";
import { deviceIdSelector, userIdSelector } from "../../app/main/mainSlice";
import { useAppSelector } from "../../app/store/hooks";
import useLogRecipeEdit from "../../hooks/useLogRecipeEdit";
import { ErrorManagementContext } from "../../utils/errorManagement/ErrorManagementProvider";
import { EventLogsEnum } from "../../utils/events";
import { UPCONNECT_TASK } from "./UpConnect.constants";

const ConnectContext = createContext<{
  deviceTask: any;
  deviceTaskStatus: any;
  updateTask: () => Promise<FetchResult<any> | void>;
  openRecipe: (recipeId: string, recipeName: string) => Promise<void>;
  cancelOpeningRecipe: (recipeId: string, recipeName: string) => Promise<void>;
  openRecipeLoading: boolean;
  cancelOpeningRecipeLoading: boolean;
}>({
  deviceTask: null,
  deviceTaskStatus: null,
  updateTask: async () => { },
  openRecipe: async (recipeId: string, recipeName: string) => { },
  cancelOpeningRecipe: async (recipeId: string, recipeName: string) => { },
  openRecipeLoading: false,
  cancelOpeningRecipeLoading: false,
});

const UpConnect = ({ children }: { children: ReactNode }): JSX.Element => {
  const deviceId = useAppSelector(deviceIdSelector);
  const userId = useAppSelector(userIdSelector)

  const { cookClubLog } = useContext(ErrorManagementContext);

  const [updateTask] = useMutation(UP_CONNECT_DEVICE_UPDATE_TASK);

  const [getUserConnectedDevice] = useLazyQuery(GET_DEVICES_CONNECTED_TO_USER, {
    variables: { id: userId },
    fetchPolicy: 'network-only',
  })

  const { logRecipeEdit } = useLogRecipeEdit();

  const [deviceTask, setDeviceTask] = useState(null);
  const [deviceTaskStatus, setDeviceTaskStatus] = useState(null);
  const [openRecipeLoading, setOpenRecipeLoading] = useState(false);
  const [cancelOpeningRecipeLoading, setCancelOpeningRecipeLoading] = useState(false);

  const { loading, error } = useSubscription(UP_CONNECT_DEVICE_SUBSCRIPTION, {
    variables: { _eq: deviceId },
    shouldResubscribe: true,
    onSubscriptionData: ({ subscriptionData }) => {
      // add salt and hashes
      const task = subscriptionData?.data?.Connect[0]?.deviceQueue[0];
      const deviceQueueStatus = subscriptionData?.data?.Connect[0]?.deviceQueueConnectionStatus;
      if (task) {
        setDeviceTask(task);
        setDeviceTaskStatus(deviceQueueStatus);
      }

    },
    fetchPolicy: 'network-only',
    onError: (err) => { console.error(err) }
  })

  const handleDeviceCheck = (fetchedDeviceId: string | null) => {
    if (!fetchedDeviceId) {
      cookClubLog("No upliance linked to your account!", "", {}, { ui: { type: "error" }, browserConsole: true });
      return false;
    }
    return true;
  };

  const handleSendingRecipeError = (recipeId: string, err: string | unknown) => {
    logRecipeEdit(EventLogsEnum.APP_ERROR_OPEN_ON_DEVICE, {
      eventData: { recipeId, err }
    })
    cookClubLog("Error sending recipe!", "", {}, { ui: { type: "error" }, browserConsole: true });
  }

  const openRecipe = async (recipeId: string, recipeName: string) => {
    logRecipeEdit(EventLogsEnum.APP_OPEN_ON_DEVICE, {
      eventData: { recipeId }
    })

    setOpenRecipeLoading(true);
    try {
      const deviceConnected = await getUserConnectedDevice().catch((err) => {
        handleSendingRecipeError(recipeId, err);
      });
      const device = deviceConnected?.data?.users_by_pk?.devices[0] ?? null;

      const isDeviceConnected = handleDeviceCheck(device?.id);
      if (!isDeviceConnected) {
        setOpenRecipeLoading(false);
        return;
      }

      await updateTask({
        variables: {
          _eq: deviceId,
          device_queue: [{
            id: UPCONNECT_TASK.OPEN_RECIPE,
            metadata: {
              recipeId,
              recipeName
            }
          }],
          device_queue_connection_status: "PENDING",
          updated_at_device_queue: new Date().toISOString(),
        }
      }).then((data) => {
        setOpenRecipeLoading(false);
        if (data?.data?.update_Connect && data?.data?.update_Connect.affected_rows > 0) {
          cookClubLog("Recipe sent to your upliance successfully!", "", {}, { ui: { type: "info" }, browserConsole: true });
        } else {
          handleSendingRecipeError(recipeId, 'Affected rows not found / were zero');
        }
      }).catch((err) => {
        handleSendingRecipeError(recipeId, err);
      })
    } catch (err) {
      setOpenRecipeLoading(false);
      // handle write error
      handleSendingRecipeError(recipeId, err);
    }
  }

  const cancelOpeningRecipe = async (recipeId: string, recipeName: string) => {
    logRecipeEdit(EventLogsEnum.APP_CANCEL_OPEN_ON_DEVICE, {
      eventData: { recipeId }
    })
    setCancelOpeningRecipeLoading(true);
    try {
      await updateTask({
        variables: {
          _eq: deviceId,
          device_queue: [{ id: UPCONNECT_TASK.RESET, metadata: { recipeId, recipeName } }],
          device_queue_connection_status: null,
          updated_at_device_queue: new Date().toISOString(),
        }
      }).then(() => {
        setCancelOpeningRecipeLoading(false);
        cookClubLog("Cancelled successfully!", "", {}, { ui: { type: "info" }, browserConsole: true });
      })
    } catch (err) {
      cookClubLog("Error cancelling!", "", {}, { ui: { type: "error" }, browserConsole: true });
      setCancelOpeningRecipeLoading(false);
    }
  }

  return (
    <ConnectContext.Provider value={{ deviceTask, deviceTaskStatus, updateTask, openRecipe, cancelOpeningRecipe, openRecipeLoading, cancelOpeningRecipeLoading }}>
      {children}
    </ConnectContext.Provider>
  )
};

export const useConnect = () => {
  const context = useContext(ConnectContext);
  if (context === undefined) {
    console.error("useConnect must be used within a UpConnect provider");
  }
  return context;
}
export default UpConnect;